/**
 * Chase queue — MONO-111 phase 2b (ADR-0035, OMON-317 Board 2).
 *
 * The office's read-and-act surface over the chase ledger: every open
 * `complete-dsr` case (or, via the header toggle, every open
 * `complete-shift-record` case — ADR-0035 am.6), oldest debt first, with the three human actions the
 * model allows (complete-manually / cancel-with-reason / reassign) and the
 * derived 3-strike watch. All data and all writes go through omon-api's
 * `/v1/chase_queue` (ADR-0033 callable wire protocol — same auth, same
 * errors as a Firebase callable; only the host differs). The page persists
 * nothing and derives nothing the API didn't: age comes from `dueEnd` and `shiftEnd`,
 * strikes arrive computed, and a cancelled case simply stops appearing.
 */
import { getFunctions, httpsCallableFromURL, FunctionsError } from "firebase/functions";
import { app } from "../firebase";
import { ageLabel } from "./age";
import {
  closedChip,
  ESCALATION_STEPS,
  finalReminderTag,
  groupByCarer,
  onlyCciCarers,
  type CarerGroup,
  type ClosedRow,
  type EscalationState,
  type EscalationStep,
  type FinalReminder,
} from "./by-carer";
import { diagnosisChip } from "./diagnosis";
import { carerFormLinks, formLinksOf, type FormLink } from "./forms";
import { cascadingOptions, passesDateRange, passesFilters, pruneDownstream, shiftDateOf, type DateRange } from "./filters";
import { isOtherThanPrimary, roleLabel, type ShiftRole } from "./role";
import { noteAuthor, noteStamp } from "./notes";
import { DEFAULT_SORT, nextSort, sortRows, type SortChoice, type SortKey } from "./sort";

const OMON_API_CHASE_URL = "https://omon-api-myhmkw6jkq-nw.a.run.app/v1/chase_queue";

interface Slot {
  date: string | null;
  session: string | null;
  start: number | null;
  finish: number | null;
  payrollRows: number | null;
}

interface Candidate {
  oneTouchCarerId: number | null;
  personId: string | null;
  name: string | null;
}

interface QueueRow {
  caseId: string;
  clientName: string | null;
  oneTouchClientId: number | null;
  /** The client's service line (omon-api walks area team → service-line). */
  service?: string | null;
  slots: Slot[];
  dueEnd: string | null;
  /** When the shift ended (the case period start); older API responses lack it. */
  shiftEnd?: string | null;
  businessStatus: string | null;
  status: string;
  upstreamGone: boolean;
  diagnosis?: string | null;
  /** The client's MachForms DSR form ids (omon-api); absent from an older
   *  API and empty on shift-record cases: no link shown. */
  formIds?: string[];
  /** OMON-360: the role the carer declared (shift records judged per person). */
  shiftRole?: ShiftRole | null;
  owner: { personId: string; name: string | null; provenance: string | null } | null;
  candidates: Candidate[];
  authoredOn: string | null;
  lastNote: { text: string; at: string | null; byEmail: string | null } | null;
  /** The whole trail, newest first (omon-api). */
  notes?: { text: string; at: string | null; byEmail: string | null; system?: boolean }[];
}

interface QueueResponse {
  rows: QueueRow[];
  strikes: { personId: string; name: string | null; count: number }[];
  kind?: CaseKind;
  /** The office's Chase start date (`_ops/chase_config.queueSince`): shifts
   *  before it are not in `rows` at all (omon-api). */
  queueSince?: string | null;
  generatedAt: string;
  /** withHistory only (omon-api): the last month's closed cases, each
   *  carer's escalation, and the coded not-owed reasons. */
  closed?: ClosedRow[];
  escalations?: Record<string, EscalationState>;
  /** withHistory only: when each carer was last sent a final reminder
   *  (absent from an older API: no tag shown). */
  finalReminders?: Record<string, FinalReminder>;
  notOwedReasons?: { code: string; display: string }[];
}

/** The office can read the queue case by case or carer by carer (Tracey's
 *  workbook was by support worker). Remembered in this browser. */
type ChaseView = "shift" | "carer";
const VIEW_PREF_KEY = "chase.view";
let view: ChaseView = "shift";
try {
  if (localStorage.getItem(VIEW_PREF_KEY) === "carer") view = "carer";
} catch {
  /* private window / blocked storage: by shift stands */
}
/** Which carer cards are open, for the page's life. Cards at or over the
 *  line start open; everything else starts folded. */
const toggled = new Set<string>();
let hotOnly = false;
// CCI filter (Tracey, 2026-11-02): remove with the old CCI process.
let cciOnly = false;
/** OMON-360 spot-check: only carers who chose a role other than primary. */
let otherRolesOnly = false;

/** The not-owed reasons, if an older API sends none. Shadow shift is
 *  deliberately absent until the policy is settled. */
const DEFAULT_REASONS = [
  { code: "client-in-hospital", display: "Client in hospital" },
  { code: "shift-did-not-happen", display: "Shift didn't happen" },
  { code: "received-elsewhere", display: "Received elsewhere (verified)" },
  { code: "other", display: "Other" },
];

/** ADR-0035 am.6: a shift owes a LIST — the DSR (always; cleared only by a
 *  MachForm) and, where a plan applies and the carer is Soma-trained, a shift
 *  record (cleared by a complete in-app record). One ledger per kind. */
type CaseKind = "complete-dsr" | "complete-shift-record";

const KINDS: { kind: CaseKind; label: string; noun: string }[] = [
  { kind: "complete-dsr", label: "DSRs", noun: "DSR" },
  { kind: "complete-shift-record", label: "Shift records", noun: "shift record" },
];

const callable = httpsCallableFromURL<Record<string, unknown>, QueueResponse | Record<string, unknown>>(
  getFunctions(app, "europe-west2"),
  OMON_API_CHASE_URL,
);

// ── formatting ──────────────────────────────────────────────────────────

function hhmm(mins: number | null): string {
  if (mins == null) return "—";
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/** Service / client / carer filters (Ed, 2026-09-24). "" = any. Held for the
 *  page's life and kept across a DSR / shift-record switch; not remembered
 *  between visits, so nobody opens the queue to a stale narrowed view. */
const filters = { service: "", client: "", carer: "" };

/** Shift-date range (Tali/Tracey, 2026-09-28): "" = open-ended. Unlike the
 *  pickers above it IS remembered in this browser — Tracey's "from" is her
 *  working window, re-set every visit it would be friction. */
const DATE_PREF_KEY = "chase.dateRange";
const dateRange: DateRange = { from: "", to: "" };
try {
  const saved = JSON.parse(localStorage.getItem(DATE_PREF_KEY) ?? "{}");
  if (typeof saved.from === "string") dateRange.from = saved.from;
  if (typeof saved.to === "string") dateRange.to = saved.to;
} catch {
  /* private window / blocked storage / bad JSON: open range stands */
}
function saveDateRange(): void {
  try {
    localStorage.setItem(DATE_PREF_KEY, JSON.stringify(dateRange));
  } catch {
    /* not worth telling anyone about */
  }
}

/** Column order (Tali, 2026-10-14: "order by column name"). For the page's
 *  life; opening the queue again starts from the oldest deadline. */
let sort: SortChoice = DEFAULT_SORT;

/** "2026-10-06" → "Mon 6 Oct" for the Shift date column. */
function shiftDay(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

/** "2026-09-23" → "23 Sep 2026" for the office start-date note. */
function dayLabel(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const KIND_PREF_KEY = "chase.kind";
let caseKind: CaseKind = "complete-dsr";

try {
  const saved = localStorage.getItem(KIND_PREF_KEY);
  if (KINDS.some((k) => k.kind === saved)) caseKind = saved as CaseKind;
} catch {
  /* private window / blocked storage: the DSR queue stands */
}

function kindNoun(): string {
  return KINDS.find((k) => k.kind === caseKind)?.noun ?? "record";
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

function slotLabel(s: Slot): string {
  const sess = s.session === "night" ? "Night" : "Day";
  return `${esc(s.date)} · ${sess} · ${hhmm(s.start)}–${hhmm(s.finish)}`;
}

// ── rendering ───────────────────────────────────────────────────────────

function container(): HTMLElement | null {
  return document.getElementById("chaseView");
}

export function renderChaseLoading(): void {
  const el = container();
  if (el) el.innerHTML = `<div class="empty-state"><h3>Loading chase queue…</h3></div>`;
}

function renderError(message: string): void {
  const el = container();
  if (el)
    el.innerHTML = `<div class="empty-state"><h3>Couldn't load the chase queue</h3><p>${esc(message)}</p></div>`;
}

/** Owner label from PROVENANCE, never hardcoded (Ed's Miley test, 2026-09-10:
 *  an office-assigned owner rendered "(claimed)" — she never claimed anything).
 *  Vocabulary: assigned = the office said so; designated/claimed = the carer
 *  acknowledged it (OMON-342, future); anything else renders verbatim. */
function ownerLabel(provenance: string | null): string {
  if (provenance === "assigned") return "(assigned)";
  if (provenance === "designated" || provenance === "claimed") return "(claimed)";
  return provenance ? `(${provenance})` : "(owner)";
}

/** A carer's name as a "hot" link to their phone and email (Ed, 2026-09-24:
 *  the old DSR report's pop-up, so the office can chase straight from the
 *  row). No person doc = nothing to look up, so plain text. */
function carerName(personId: string | null, name: string): string {
  return personId
    ? `<button type="button" class="chase-person" data-person="${esc(personId)}" title="Phone and email">${esc(name)}</button>`
    : esc(name);
}

/** The client's DSR form link(s) (Tracey, 2026-10-30): opens the MachForm in
 *  a new tab, with a copy button for pasting into a reminder email. */
function formLinkHtml(l: FormLink): string {
  return `<span class="chase-form"><a class="chase-form-link" href="${esc(l.url)}" target="_blank" rel="noopener noreferrer" title="Open the client's DSR MachForm (form ${esc(l.formId)}) in a new tab">${esc(l.label)}</a>` +
    `<button type="button" class="chase-copy" data-copy="${esc(l.url)}" title="Copy the link, to paste into a reminder email">Copy link</button></span>`;
}

function formsHtml(links: FormLink[]): string {
  return links.length ? `<div class="chase-forms">${links.map(formLinkHtml).join("")}</div>` : "";
}

function rowHtml(r: QueueRow): string {
  const age = ageLabel(r.dueEnd, r.shiftEnd ?? null);
  const who = r.owner
    ? `${carerName(r.owner.personId, r.owner.name ?? r.owner.personId)} <span class="chase-soft">${esc(ownerLabel(r.owner.provenance))}</span>`
    : r.candidates.length
      ? r.candidates.map((c) => carerName(c.personId, c.name ?? `OT ${c.oneTouchCarerId}`)).join(" <span class=\"chase-soft\">or</span> ") +
        ' <span class="chase-soft">(unclaimed)</span>'
      : '<span class="chase-unassigned">unassigned — no carer on the roster row</span>';
  // OMON-360: the carer's declared role — their claim, for the office to check.
  const role = roleLabel(r.shiftRole);
  const whoCell = role
    ? `${who} <button type="button" class="chase-role" data-act="role" title="The role the carer chose in the app. Their claim: click to correct it.">${esc(role)}</button>`
    : who;
  // Why it's still owed (hourly reconciler label; tooltip explains).
  const d = diagnosisChip(r.diagnosis);
  const chip = d
    ? ` <span class="chase-diag ${d.tone}" data-tip="${esc(d.tip)}" title="${esc(d.tip)}">${esc(d.label)}</span>`
    : "";
  // Honest fallback (OwedRow convention): a nameless client renders as a
  // fault to fix, never a blank.
  const client = r.clientName
    ? esc(r.clientName)
    : `<span class="chase-unassigned">Client not identified</span> <span class="chase-soft">(OT ${esc(r.oneTouchClientId)})</span>`;
  // The note NARRATIVE (Ed, 2026-09-22): every note ever added, each in its
  // own timestamped bubble, newest on the left, wrapping as it grows. Notes
  // are never edited; audit entries (e.g. "assignment cleared") sit in the
  // trail too, shown quieter. Falls back to lastNote for an older API.
  const trail = r.notes ?? (r.lastNote ? [r.lastNote] : []);
  const note = trail.length
    ? `<div class="chase-notes">${trail.map((n) => {
        const sys = "system" in n && n.system;
        const who = noteAuthor(n.byEmail);
        return `<span class="chase-note${sys ? " system" : ""}">${sys ? "" : "✎ "}${esc(n.text)}` +
          ` <span class="chase-soft">· ${esc(noteStamp(n.at))}${who ? ` · ${esc(who)}` : ""}</span></span>`;
      }).join("")}</div>`
    : "";
  // Full-width (Ed, 2026-09-22): in the narrow client column the bubbles
  // stacked one per line; on their own row spanning the table they sit side
  // by side and wrap only when the trail outgrows the width.
  const notesRow = note
    ? `<tr class="chase-notes-row" data-case="${esc(r.caseId)}"><td colspan="5">${note}</td></tr>`
    : "";
  return `<tr data-case="${esc(r.caseId)}"${note ? ' class="has-notes"' : ""}>
    <td><div class="chase-client">${client}${r.upstreamGone ? ' <span class="chase-gone">shift changed upstream</span>' : ""}${chip}</div>
        <div class="chase-soft">${r.slots.map(slotLabel).join(" · ")}</div>${formsHtml(formLinksOf(r))}</td>
    <!-- Shift date in its own column (Tali, 2026-10-14). It replaced Rung,
         which only ever read "awaiting" until reminders exist (ADR-0035
         ladder); bring Rung back when something moves cases up it. -->
    <td class="chase-date">${esc(shiftDay(shiftDateOf(r)))}</td>
    <td>${whoCell}</td>
    <td class="${age.overdue ? "chase-overdue" : "chase-due"}">${esc(age.text)}</td>
    <td class="chase-actions"><div class="chase-actions-grid">
      <!-- Two by two (Ed, 2026-09-22): in one line the four buttons took
           ~370px and squeezed "Owed by" to ~100px. -->
      <!-- "Resolve", not "Complete" (Ed): "Complete manually" read as
           "fill in the DSR data yourself" — the button SETTLES the case,
           it doesn't author the record. Wire action stays "complete". -->
      <button data-act="complete">Resolve manually</button>
      <button data-act="note">Note…</button>
      <!-- "Not owed…" (Tracey's workbook → Chase, 2026-10-13): a coded
           reason, so the month's not-owed cases can be counted by why. -->
      <button data-act="cancel">Not owed…</button>
      <!-- "(Re)assign carer" (Ed, 2026-09-10, twice-refined): the same button
           both SETS the first owner on an unclaimed case and re-aims a
           claimed one, and the label names the OBJECT — on a row full of
           client names, "(Re)assign" alone left what gets assigned to
           the reader's guess. -->
      <button data-act="reassign">(Re)assign carer</button>
    </div></td>
  </tr>${notesRow}`;
}

// ── by carer (Tracey's workbook → Chase, 2026-10-13) ───────────────────

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "client", label: "Client / shift" },
  { key: "date", label: "Shift date" },
  { key: "carer", label: "Owed by" },
  { key: "age", label: "Age" },
];

/** Every column sorts on click (Tali, 2026-10-14); the arrow shows which. */
function tableHead(): string {
  return `<thead><tr>${COLUMNS.map(({ key, label }) => {
    const on = sort.key === key;
    const arrow = on ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
    const aria = on ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
    return `<th aria-sort="${aria}"><button type="button" class="chase-sort${on ? " on" : ""}" data-sort="${key}">${esc(label)}${arrow}</button></th>`;
  }).join("")}<th></th></tr></thead>`;
}

function isUnfolded(g: CarerGroup<QueueRow>): boolean {
  return toggled.has(g.key) ? !g.hot : g.hot;
}

/** "30 October 2026 14:05", for the tag's tooltip (the CCI notes want the
 *  full date). */
function fullStamp(at: string | null): string {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  return `${day} ${noteStamp(at).split(" ").pop()}`;
}

/** Final reminder sent (Tracey, 2026-10-30): read BEFORE the escalation
 *  steps, the order things happen. A manual marker; nothing is emailed. */
function finalReminderHtml(reminder: FinalReminder | undefined, compact: boolean): string {
  const tag = finalReminderTag(reminder);
  if (compact) return tag ? `<span class="chase-closed-chip ok">${esc(tag)}</span>` : "";
  if (!tag) {
    return `<span class="chase-esc-label">Final reminder</span>` +
      `<button type="button" data-rem="record" title="Record that you sent this carer their final reminder today. Nothing is emailed.">+ Final reminder sent</button>`;
  }
  const who = noteAuthor(reminder?.byEmail ?? null);
  const when = fullStamp(reminder?.at ?? null);
  return `<span class="chase-esc-label">Final reminder</span>` +
    `<button type="button" class="set" data-rem="clear" title="Recorded ${esc(when)}${who ? ` by ${esc(who)}` : ""}. Click to clear.">${esc(tag)}${who ? ` · ${esc(who)}` : ""}</button>` +
    `<button type="button" data-rem="record" title="Record that another final reminder went today. The date moves on; the earlier one is kept in the audit trail.">Sent again today</button>`;
}

function escalationHtml(
  personId: string | null,
  state: EscalationState | undefined,
  compact: boolean,
  reminder?: FinalReminder,
): string {
  if (!personId) return "";
  const parts = ESCALATION_STEPS.map(({ step, label }) => {
    const s = state?.steps?.[step];
    if (compact) return s ? `<span class="chase-closed-chip ok">${esc(label)} · ${esc(noteStamp(s.at))}</span>` : "";
    const who = s ? noteAuthor(s.byEmail) : "";
    return s
      ? `<button type="button" class="set" data-esc="${step}" data-isset="1" title="Set ${esc(noteStamp(s.at))}${who ? ` by ${esc(who)}` : ""}. Click to clear.">${esc(label)} · ${esc(noteStamp(s.at))}${who ? ` · ${esc(who)}` : ""}</button>`
      : `<button type="button" data-esc="${step}">+ ${esc(label)}</button>`;
  }).join("");
  const rem = finalReminderHtml(reminder, compact);
  if (compact) return rem + parts;
  return `<div class="chase-esc" data-person="${esc(personId)}">${rem}<span class="chase-esc-label">Escalation</span>${parts}</div>`;
}

/** A carer card's DSR form links, one line per owed client. */
function carerFormsHtml(rows: QueueRow[]): string {
  const clients = carerFormLinks(rows);
  if (!clients.length) return "";
  return `<div class="chase-closed chase-card-forms"><span class="chase-closed-label">DSR forms</span>${clients.map((c) =>
    `<span class="chase-form-client">${esc(c.client)} ${c.links.map(formLinkHtml).join("")}</span>`).join("")}</div>`;
}

function closedHtml(rows: ClosedRow[]): string {
  if (!rows.length) return "";
  return `<div class="chase-closed"><span class="chase-closed-label">Closed this month</span>${rows.map((c) => {
    const chip = closedChip(c);
    const when = c.slots[0]?.date ? ` · ${esc(c.slots[0].date)}` : "";
    const who = noteAuthor(c.byEmail ?? null);
    return `<span class="chase-closed-chip ${chip.tone}" title="Closed ${esc(noteStamp(c.closedAt))}${who ? ` by ${esc(who)}` : ""}">${esc(c.clientName ?? "Client not identified")}${when} — ${esc(chip.text)}</span>`;
  }).join("")}</div>`;
}

function groupHtml(g: CarerGroup<QueueRow>, data: QueueResponse): string {
  const unfolded = isUnfolded(g);
  const state = g.personId ? data.escalations?.[g.personId] : undefined;
  const reminder = g.personId ? data.finalReminders?.[g.personId] : undefined;
  const n = g.open.length;
  const who = g.personId ? carerName(g.personId, g.name) : esc(g.name);
  return `<section class="chase-group${g.hot ? " hot" : ""}">
    <div class="chase-group-head" data-group="${esc(g.key)}" aria-expanded="${unfolded}">
      <div class="chase-group-who"><strong>${who}</strong><span class="chase-soft">${esc(g.services.join(" · ") || "No service recorded")}</span></div>
      <span class="chase-count-pill">${n} missing${g.hot ? " · at or over 3" : ""}</span>
      ${unfolded ? "" : escalationHtml(g.personId, state, true, reminder)}
      <span class="chase-group-toggle">${unfolded ? "Hide" : n ? `Show ${n} ${n === 1 ? "case" : "cases"}` : "Show closed cases"}</span>
    </div>
    ${unfolded ? `${escalationHtml(g.personId, state, false, reminder)}
      ${carerFormsHtml(g.open)}
      ${n ? `<div class="chase-table-wrap"><table class="chase-table">${tableHead()}<tbody>${g.open.map(rowHtml).join("")}</tbody></table></div>` : ""}
      ${closedHtml(g.closed)}` : ""}
  </section>`;
}

function carerBody(shown: QueueRow[], data: QueueResponse): string {
  let groups = groupByCarer(shown, data.closed ?? []);
  // CCI filter (Tracey, 2026-11-02): remove with the old CCI process.
  if (cciOnly) groups = onlyCciCarers(groups, data.rows, otherRolesOnly ? [] : data.closed ?? [], data.escalations, filters, dateRange);
  if (hotOnly) groups = groups.filter((g) => g.hot);
  if (cciOnly && !groups.length)
    return `<div class="empty-state"><h3>No carer here has a CCI raised</h3><p>Untick "CCI raised" to see everyone.</p></div>`;
  if (!groups.length)
    return `<div class="empty-state"><h3>No carer is at 3 or more</h3><p>Untick "Only carers at 3 or more" to see everyone.</p></div>`;
  return `<div class="chase-groups">${groups.map((g) => groupHtml(g, data)).join("")}</div>`;
}

// ── refresh ─────────────────────────────────────────────────────────────
// Stream (30s auto-refresh) removed 2026-09-22 (Ed). The queue is rebuilt by
// the hourly reconciler, so a 30s poll only re-fetched the same data; the
// manual Refresh button stays for after an office action.

/** Panel ENTRY point (navigation.ts). renderChaseQueue alone re-renders. */
export async function enterChaseQueue(): Promise<void> {
  renderChaseLoading();
  await renderChaseQueue();
}

export async function renderChaseQueue(): Promise<void> {
  // No loading splash here: entry shows it once (enterChaseQueue); a
  // refresh keeps the current table until fresh data lands.
  const data = await fetchQueue();
  if (data) paint(data);
}

async function fetchQueue(): Promise<QueueResponse | null> {
  try {
    return (await callable({ action: "list", kind: caseKind, withHistory: true })).data as QueueResponse;
  } catch (e) {
    renderError(e instanceof FunctionsError ? e.message : String(e));
    return null;
  }
}

function paint(data: QueueResponse): void {
  const el = container();
  if (!el) return;
  if (data.notOwedReasons?.length) reasons = data.notOwedReasons;

  const strikes = data.strikes.length
    ? `<div class="chase-strikes"><span class="chase-strikes-label">3-strike watch · derived live, stored nowhere</span>
       ${data.strikes.map((s) => `<span class="chase-striker">${esc(s.name ?? s.personId)} — ${s.count} confirmed</span>`).join(" ")}
       <span class="chase-soft">confirmed debtors only — cancelled cases never count</span></div>`
    : "";

  const updatedAt = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  // No lateness slider (Tali/Tracey, Ed 2026-09-30): every open case shows,
  // from the moment its shift ends; the dates and pickers narrow it.
  const rows = data.rows;
  const shown = sortRows(rows.filter((r) => passesFilters(r, filters) && passesDateRange(r, dateRange)
    && (!otherRolesOnly || isOtherThanPrimary(r.shiftRole))), sort);
  const anyRoles = rows.some((r) => r.shiftRole);
  const filtered = rows.length - shown.length;
  const anyFilter = Boolean(filters.service || filters.client || filters.carer || dateRange.from || dateRange.to || otherRolesOnly);
  const body = shown.length || (view === "carer" && cciOnly)
    ? view === "carer"
      ? `${carerBody(shown, data)}${strikes}`
      : `<div class="chase-table-wrap"><table class="chase-table">
      ${tableHead()}
      <tbody>${shown.map(rowHtml).join("")}</tbody>
    </table></div>
    ${strikes}`
    : rows.length
      ? `<div class="empty-state"><h3>Nothing matches these filters</h3>
      <p>${rows.length} open ${rows.length === 1 ? "case is" : "cases are"} outside the service, client, carer or dates you picked.</p></div>`
      : `<div class="empty-state"><h3>No open cases</h3>
      <p>Every owed ${esc(kindNoun())} is accounted for. Cases appear here once a shift ends without one.</p></div>`;

  // Options come from every open case, each with a count; a choice that no
  // longer has rows stays listed (at 0) so it can be seen and cleared rather
  // than silently vanishing.
  const select = (which: "service" | "client" | "carer", label: string, anyLabel: string) => {
    const opts = cascadingOptions(rows, which, filters);
    const cur = filters[which];
    if (cur && !opts.some((o) => o.key === cur)) opts.unshift({ key: cur, label: "(no longer in this view)", count: 0 });
    return `<label class="chase-select"><span>${esc(label)}</span>
      <select data-filter="${which}" aria-label="Filter by ${esc(label.toLowerCase())}">
        <option value="">${esc(anyLabel)}</option>
        ${opts.map((o) => `<option value="${esc(o.key)}"${o.key === cur ? " selected" : ""}>${esc(o.label)} (${o.count})</option>`).join("")}
      </select></label>`;
  };
  const pickers = `
    <div class="chase-filter chase-pickers">
      ${select("service", "Service", "All services")}
      ${select("client", "Client", "All clients")}
      ${select("carer", "Carer", "All carers")}
      <div class="chase-dates">
        <label class="chase-select"><span>Shift from</span>
          <input type="date" data-date="from" value="${esc(dateRange.from)}" aria-label="Shifts on or after"></label>
        <label class="chase-select"><span>To</span>
          <input type="date" data-date="to" value="${esc(dateRange.to)}" aria-label="Shifts on or before"></label>
        ${anyFilter ? `<button type="button" class="chase-clear-filters">Clear filters</button>` : ""}
        ${filtered ? `<span class="chase-soft">${filtered} filtered out</span>` : ""}
        ${anyRoles || otherRolesOnly ? `<label class="chase-hot-only"><input type="checkbox" id="chaseOtherRoles"${otherRolesOnly ? " checked" : ""}> Only roles other than primary</label>` : ""}
        ${view === "carer" ? `<label class="chase-hot-only"><input type="checkbox" id="chaseHotOnly"${hotOnly ? " checked" : ""}> Only carers at 3 or more</label>
          <label class="chase-hot-only"><input type="checkbox" id="chaseCciOnly"${cciOnly ? " checked" : ""}> CCI raised</label>` : ""}
      </div>
    </div>`;
  const viewToggle = `<div class="chase-views" role="group" aria-label="View">
      <button type="button" data-view="shift" aria-pressed="${view === "shift"}">By shift</button>
      <button type="button" data-view="carer" aria-pressed="${view === "carer"}">By carer</button>
    </div>`;
  const kindToggle = `<div class="chase-kinds" role="group" aria-label="Which record">
      ${KINDS.map((k) => `<button type="button" data-kind="${k.kind}" aria-pressed="${k.kind === caseKind}">${esc(k.label)}</button>`).join("")}
    </div>`;
  el.innerHTML = `
    <div class="chase-head"><h2>Chase queue</h2>${kindToggle}<span class="chase-count">${shown.length} open</span>
      <span class="chase-soft">updated ${updatedAt}</span>
      ${data.queueSince ? `<span class="chase-soft" title="Office setting: shifts before this date are not chased here">Chase starts ${esc(dayLabel(data.queueSince))}</span>` : ""}
      <!-- Refresh, lifted from the Connect timeline (Ed, 2026-09-10). -->
      <button class="connect-icon-btn" type="button" id="chaseRefreshBtn" data-tip="Refresh" aria-label="Refresh">
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M20 12a8 8 0 1 1-2.34-5.66M20 4v4h-4"/></svg>
      </button>
      ${viewToggle}
    </div>
    ${pickers}
    ${body}`;

  // By shift / By carer: same data, repainted; remembered in this browser.
  el.querySelectorAll<HTMLButtonElement>(".chase-views button[data-view]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = btn.dataset.view as ChaseView;
      if (next === view) return;
      view = next;
      try {
        localStorage.setItem(VIEW_PREF_KEY, view);
      } catch {
        /* not worth telling anyone about */
      }
      paint(data);
    });
  });
  el.querySelector<HTMLInputElement>("#chaseOtherRoles")?.addEventListener("change", (e) => {
    otherRolesOnly = (e.currentTarget as HTMLInputElement).checked;
    paint(data);
  });
  el.querySelector<HTMLInputElement>("#chaseHotOnly")?.addEventListener("change", (e) => {
    hotOnly = (e.currentTarget as HTMLInputElement).checked;
    paint(data);
  });
  el.querySelector<HTMLInputElement>("#chaseCciOnly")?.addEventListener("change", (e) => {
    cciOnly = (e.currentTarget as HTMLInputElement).checked;
    paint(data);
  });
  // A card's head folds and unfolds it (not its name, which opens contact).
  el.querySelectorAll<HTMLElement>(".chase-group-head[data-group]").forEach((head) => {
    head.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest("button")) return;
      const key = head.dataset.group as string;
      if (toggled.has(key)) toggled.delete(key);
      else toggled.add(key);
      paint(data);
    });
  });
  el.querySelectorAll<HTMLButtonElement>(".chase-esc button[data-esc]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const personId = btn.closest<HTMLElement>(".chase-esc")?.dataset.person;
      if (personId) void runEscalation(personId, btn.dataset.esc as EscalationStep, btn.dataset.isset === "1");
    });
  });
  el.querySelectorAll<HTMLButtonElement>(".chase-esc button[data-rem]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const personId = btn.closest<HTMLElement>(".chase-esc")?.dataset.person;
      if (personId) void runFinalReminder(personId, btn.dataset.rem === "clear");
    });
  });
  // Copy a DSR form link for a reminder email; says so on the button.
  el.querySelectorAll<HTMLButtonElement>("button.chase-copy[data-copy]").forEach((btn) => {
    btn.addEventListener("click", () => void copyLink(btn));
  });

  // The pickers filter rows already in hand: instant
  // repaint, focus kept on the control that changed.
  el.querySelectorAll<HTMLSelectElement>("select[data-filter]").forEach((sel) => {
    sel.addEventListener("change", () => {
      const which = sel.dataset.filter as "service" | "client" | "carer";
      filters[which] = sel.value;
      // Cascading (Tali, 2026-10-14): a new service or client clears a
      // choice below it that no longer belongs to it.
      Object.assign(filters, pruneDownstream(data.rows, filters, which));
      paint(data);
      el.querySelector<HTMLSelectElement>(`select[data-filter="${which}"]`)?.focus();
    });
  });
  // Column headers sort (Tali, 2026-10-14): click again to reverse.
  el.querySelectorAll<HTMLButtonElement>("button.chase-sort[data-sort]").forEach((btn) => {
    btn.addEventListener("click", () => {
      sort = nextSort(sort, btn.dataset.sort as SortKey);
      paint(data);
      el.querySelector<HTMLButtonElement>(`button.chase-sort[data-sort="${sort.key}"]`)?.focus();
    });
  });
  el.querySelector(".chase-clear-filters")?.addEventListener("click", () => {
    filters.service = filters.client = filters.carer = "";
    dateRange.from = dateRange.to = "";
    otherRolesOnly = false;
    saveDateRange();
    paint(data);
  });
  // Shift-date range: filters rows in hand, remembered in this browser.
  el.querySelectorAll<HTMLInputElement>("input[data-date]").forEach((inp) => {
    inp.addEventListener("change", () => {
      const which = inp.dataset.date as "from" | "to";
      dateRange[which] = inp.value;
      saveDateRange();
      paint(data);
      el.querySelector<HTMLInputElement>(`input[data-date="${which}"]`)?.focus();
    });
  });

  // A carer's name opens their phone and email.
  el.querySelectorAll<HTMLButtonElement>("button.chase-person[data-person]").forEach((btn) => {
    btn.addEventListener("click", () => void showContact(btn.dataset.person as string, btn.textContent ?? ""));
  });

  // Switching ledger re-reads from the API (each kind is its own list); the
  // choice sticks for this browser.
  el.querySelectorAll<HTMLButtonElement>(".chase-kinds button[data-kind]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = btn.dataset.kind as CaseKind;
      if (next === caseKind) return;
      caseKind = next;
      try {
        localStorage.setItem(KIND_PREF_KEY, caseKind);
      } catch {
        /* not worth telling anyone about */
      }
      void enterChaseQueue();
    });
  });

  // Refresh must SHOW that it acted (Ed, 2026-09-10: "neither icon visibly
  // does anything"): it spins while in flight, with the three-rotation
  // minimum (OMON-290 — an instant response must still read as an action).
  el.querySelector("#chaseRefreshBtn")?.addEventListener("click", (ev) => {
    const btn = ev.currentTarget as HTMLButtonElement;
    btn.classList.add("spinning");
    const minSpin = new Promise((res) => setTimeout(res, 2100)); // 3 × 700ms
    void Promise.all([fetchQueue(), minSpin]).then(([fresh]) => {
      if (fresh) paint(fresh); // repaint replaces the button, ending the spin
      else btn.classList.remove("spinning"); // error path painted its own view
    });
  });

  el.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const caseId = btn.closest("tr")?.getAttribute("data-case");
      if (caseId) void runAction(btn.dataset.act as string, caseId, data.rows);
    });
  });
}

// ── actions (write HUMAN soil only; the API enforces open-status) ───────

async function runAction(act: string, caseId: string, rows: QueueRow[]): Promise<void> {
  const row = rows.find((r) => r.caseId === caseId);
  try {
    if (act === "note") {
      // Always a NEW, timestamped note; earlier notes stay on the row.
      const prior = row?.lastNote ? `\nLatest note: ${row.lastNote.text}` : "";
      const text = window.prompt(
        `Add a note to this case. It's timestamped and added to the row; earlier notes are kept.${prior}`, "");
      if (text === null || !text.trim()) return;
      await callable({ action: "note", caseId, text });
    } else if (act === "complete") {
      const note = window.prompt(
        "Resolve manually — the record exists and you've verified it (you are not filling it in here).\nOptional note (where / how verified):", "");
      if (note === null) return; // dismissed = no action
      await callable({ action: "complete", caseId, note });
    } else if (act === "cancel") {
      // A coded reason (Tracey's workbook → Chase): countable by why, and
      // a not-owed case never counts against a carer.
      const choice = await pickNotOwed(reasons);
      if (!choice) return;
      await callable({ action: "cancel", caseId, reasonCode: choice.code, reason: choice.text });
    } else if (act === "role") {
      // OMON-360: the office corrects a carer's declared role (audited).
      const choice = await pickRole(row?.shiftRole?.code ?? "primary");
      if (!choice) return;
      await callable({ action: "set_role", caseId, code: choice.code, reason: choice.reason });
    } else if (act === "reassign") {
      const choice = await pickCarer(row?.candidates ?? [], Boolean(row?.owner));
      if (!choice) return; // dismissed
      if (choice.kind === "clear") {
        // Ed, 2026-09-10: assignment must not be a one-way door — a wrong
        // owner could previously only become a DIFFERENT wrong owner.
        await callable({ action: "unassign", caseId });
      } else {
        await callable({ action: "reassign", caseId, personId: choice.personId });
      }
    } else {
      return;
    }
  } catch (e) {
    window.alert(e instanceof FunctionsError ? e.message : String(e));
    return;
  }
  await renderChaseQueue(); // one truth: re-read rather than patch the DOM
}

/** Set or clear one escalation step for a carer. Clearing asks first; the
 *  API keeps both in the audit trail either way. */
async function runEscalation(personId: string, step: EscalationStep, isSet: boolean): Promise<void> {
  const label = ESCALATION_STEPS.find((s) => s.step === step)?.label ?? step;
  if (isSet && !window.confirm(`Clear "${label}" for this carer? The change is kept in the audit trail.`)) return;
  try {
    await callable({ action: "escalate", kind: caseKind, personId, step, clear: isSet });
  } catch (e) {
    window.alert(e instanceof FunctionsError ? e.message : String(e));
    return;
  }
  await renderChaseQueue();
}

/** Record (or clear) "Final reminder sent" for a carer. Only a marker:
 *  the reminder itself goes from the office's own mailbox. Clearing asks
 *  first, as an escalation step does; the API keeps both in the audit. */
async function runFinalReminder(personId: string, clear: boolean): Promise<void> {
  if (clear && !window.confirm(`Clear "Final reminder sent" for this carer? The change is kept in the audit trail.`)) return;
  try {
    await callable({ action: "final_reminder", kind: caseKind, personId, clear });
  } catch (e) {
    window.alert(e instanceof FunctionsError ? e.message : String(e));
    return;
  }
  await renderChaseQueue();
}

/** Copy a form link; where the browser refuses the clipboard, show the link
 *  to copy by hand rather than fail silently. */
async function copyLink(btn: HTMLButtonElement): Promise<void> {
  const url = btn.dataset.copy ?? "";
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    window.prompt("Copy this link:", url);
    return;
  }
  btn.textContent = "Copied";
  btn.classList.add("done");
  setTimeout(() => {
    btn.textContent = "Copy link";
    btn.classList.remove("done");
  }, 1500);
}

/** The coded reasons the API offers (refreshed with every list). */
let reasons: { code: string; display: string }[] = DEFAULT_REASONS;

/** Not owed: pick a reason; Other needs words, the rest may add them. */
function pickNotOwed(
  options: { code: string; display: string }[],
): Promise<{ code: string; text: string } | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "chase-modal-overlay";
    overlay.innerHTML = `
      <div class="chase-modal" role="dialog" aria-label="Not owed">
        <h3>Not owed</h3>
        <div class="chase-modal-hint">This shift doesn't owe a record. Not-owed cases never count against a carer.</div>
        <div class="chase-reasons">
          ${options.map((o) => `<label><input type="radio" name="chaseReason" value="${esc(o.code)}"> ${esc(o.display)}</label>`).join("")}
        </div>
        <div class="chase-modal-sect">Details</div>
        <input class="chase-modal-search" type="text" placeholder="Optional, except for Other" autocomplete="off">
        <div class="chase-modal-foot">
          <button class="chase-modal-cancel">Cancel</button>
          <button class="chase-modal-ok" disabled>Mark not owed</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const ok = overlay.querySelector<HTMLButtonElement>(".chase-modal-ok")!;
    const text = overlay.querySelector<HTMLInputElement>(".chase-modal-search")!;
    const picked = () => overlay.querySelector<HTMLInputElement>('input[name="chaseReason"]:checked')?.value;
    const valid = () => {
      const code = picked();
      return Boolean(code && (code !== "other" || text.value.trim()));
    };
    const refresh = () => {
      ok.disabled = !valid();
    };
    const done = (v: { code: string; text: string } | null) => {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") done(null);
      if (e.key === "Enter" && valid()) done({ code: picked() as string, text: text.value.trim() });
    };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) done(null);
    });
    overlay.querySelectorAll('input[name="chaseReason"]').forEach((r) => r.addEventListener("change", refresh));
    text.addEventListener("input", refresh);
    overlay.querySelector(".chase-modal-cancel")?.addEventListener("click", () => done(null));
    ok.addEventListener("click", () => {
      if (valid()) done({ code: picked() as string, text: text.value.trim() });
    });
  });
}

/** OMON-360: pick the role the carer actually had on this shift. */
function pickRole(current: string): Promise<{ code: string; reason: string } | null> {
  const ROLES: [string, string][] = [
    ["primary", "Primary carer"], ["secondary", "Second carer"],
    ["relief", "Relief carer"], ["shadow", "Shadowing"],
  ];
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "chase-modal-overlay";
    overlay.innerHTML = `
      <div class="chase-modal" role="dialog" aria-label="Correct the role">
        <h3>Correct the role</h3>
        <div class="chase-modal-hint">The role the carer had on this shift. It decides what they owe from the next hourly update, and their app shows it. The change is noted on the case.</div>
        <div class="chase-reasons">
          ${ROLES.map(([c, label]) => `<label><input type="radio" name="chaseRole" value="${c}"${c === current ? " checked" : ""}> ${label}</label>`).join("")}
        </div>
        <div class="chase-modal-sect">Why (optional)</div>
        <input class="chase-modal-search" type="text" placeholder="For example: the rota shows her as primary" autocomplete="off">
        <div class="chase-modal-foot">
          <button class="chase-modal-cancel">Cancel</button>
          <button class="chase-modal-ok" disabled>Correct role</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const ok = overlay.querySelector<HTMLButtonElement>(".chase-modal-ok")!;
    const text = overlay.querySelector<HTMLInputElement>(".chase-modal-search")!;
    const picked = () => overlay.querySelector<HTMLInputElement>('input[name="chaseRole"]:checked')?.value;
    const valid = () => Boolean(picked() && picked() !== current);
    const done = (v: { code: string; reason: string } | null) => {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") done(null);
      if (e.key === "Enter" && valid()) done({ code: picked() as string, reason: text.value.trim() });
    };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) done(null);
    });
    overlay.querySelectorAll('input[name="chaseRole"]').forEach((r) =>
      r.addEventListener("change", () => { ok.disabled = !valid(); }));
    overlay.querySelector(".chase-modal-cancel")?.addEventListener("click", () => done(null));
    ok.addEventListener("click", () => {
      if (valid()) done({ code: picked() as string, reason: text.value.trim() });
    });
  });
}

// ── a carer's contact card (Ed, 2026-09-24) ─────────────────────────────

interface CarerContact {
  personId: string;
  name: string | null;
  phone: string | null;
  email: string | null;
}

/** tel: wants digits and a leading +, nothing else. */
function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

/** Phone and email for one carer, fetched when the name is clicked (never
 *  sent with the queue itself). Opens straight away with the name so the
 *  click visibly did something, then fills in. */
async function showContact(personId: string, shownName: string): Promise<void> {
  const overlay = document.createElement("div");
  overlay.className = "chase-modal-overlay";
  overlay.innerHTML = `
    <div class="chase-modal" role="dialog" aria-label="Contact ${esc(shownName)}">
      <h3>${esc(shownName)}</h3>
      <div class="chase-contact"><div class="chase-modal-hint">Looking up their details…</div></div>
      <div class="chase-modal-foot"><button class="chase-modal-cancel">Close</button></div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector(".chase-modal-cancel")?.addEventListener("click", close);
  const body = overlay.querySelector<HTMLElement>(".chase-contact")!;
  try {
    const c = (await callable({ action: "contact", personId })).data as unknown as CarerContact;
    const line = (label: string, value: string | null, href: string | null) =>
      `<div class="chase-contact-line"><span class="chase-modal-sect">${esc(label)}</span>${
        value && href
          ? `<a href="${esc(href)}">${esc(value)}</a>`
          : '<span class="chase-soft">Not recorded</span>'}</div>`;
    body.innerHTML =
      line("Phone", c.phone, c.phone ? telHref(c.phone) : null) +
      line("Email", c.email, c.email ? `mailto:${c.email}` : null);
  } catch (e) {
    body.innerHTML = `<div class="chase-modal-hint">Couldn't load their details — ${esc(
      e instanceof FunctionsError ? e.message : String(e))}</div>`;
  }
}

// ── the carer picker (Ed, 2026-09-10: search-and-pick, not paste-an-id) ──

interface DirectoryCarer {
  personId: string;
  name: string;
}

/** The assignable-carer directory, fetched once per page life and cached —
 *  ~a couple of thousand names is trivially filterable client-side, and the
 *  server-side stream it comes from is the expensive half. */
let directoryCache: DirectoryCarer[] | null = null;

async function carerDirectory(): Promise<DirectoryCarer[]> {
  if (directoryCache) return directoryCache;
  const res = (await callable({ action: "carers" })).data as { carers: DirectoryCarer[] };
  directoryCache = res.carers;
  return directoryCache;
}

type PickerChoice = { kind: "pick"; personId: string } | { kind: "clear" };

/**
 * Modal picker: the case's own candidates first as one-tap choices (the
 * common case — they were on the shift), then a fast filtering search over
 * the whole carer directory beneath. Rank, don't restrict (Ed, 2026-09-10):
 * an off-roster pick is legitimate — the unassigned rows exist BECAUSE the
 * roster is sometimes wrong — but it must be deliberate, so search hits not
 * on this shift carry an explicit marker. When the case already has an
 * owner, the foot offers "Clear assignment" so assigning is never a one-way
 * door. Resolves with a choice, or null on dismiss (Escape, backdrop,
 * Cancel).
 */
function pickCarer(candidates: Candidate[], hasOwner: boolean): Promise<PickerChoice | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "chase-modal-overlay";
    const named = candidates.filter((c) => c.personId);
    const onShift = new Set(named.map((c) => c.personId));
    overlay.innerHTML = `
      <div class="chase-modal" role="dialog" aria-label="Assign to a carer">
        <h3>Assign to a carer</h3>
        ${named.length ? `
          <div class="chase-modal-sect">On this shift</div>
          <div class="chase-modal-cands">
            ${named.map((c) => `<button class="chase-cand" data-pid="${esc(c.personId)}">${esc(c.name ?? `OT ${c.oneTouchCarerId}`)}</button>`).join("")}
          </div>
          <div class="chase-modal-sect">Or anyone</div>` : ""}
        <input class="chase-modal-search" type="text" placeholder="Start typing a name…" autocomplete="off">
        <div class="chase-modal-list"><div class="chase-modal-hint">Loading carers…</div></div>
        <div class="chase-modal-foot">
          ${hasOwner ? '<button class="chase-modal-clear">Clear assignment</button>' : ""}
          <button class="chase-modal-cancel">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const done = (choice: PickerChoice | null) => {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
      resolve(choice);
    };
    const pick = (pid: string | undefined) => {
      if (pid) done({ kind: "pick", personId: pid });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") done(null);
    };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) done(null);
    });
    overlay.querySelector(".chase-modal-cancel")?.addEventListener("click", () => done(null));
    overlay.querySelector(".chase-modal-clear")?.addEventListener("click", () => done({ kind: "clear" }));
    overlay.querySelectorAll<HTMLButtonElement>(".chase-cand").forEach((b) =>
      b.addEventListener("click", () => pick(b.dataset.pid)),
    );

    const input = overlay.querySelector<HTMLInputElement>(".chase-modal-search")!;
    const list = overlay.querySelector<HTMLElement>(".chase-modal-list")!;
    input.focus();

    void carerDirectory()
      .then((all) => {
        const render = () => {
          const q = input.value.trim().toLowerCase();
          const hits = q
            ? all.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 30)
            : [];
          list.innerHTML = hits.length
            ? hits.map((c) => `<button class="chase-hit" data-pid="${esc(c.personId)}">${esc(c.name)}${
                onShift.has(c.personId) ? "" : ' <span class="chase-soft">· not on this shift</span>'
              }</button>`).join("")
            : `<div class="chase-modal-hint">${q ? "No carer matches that." : `Type to search ${all.length} carers.`}</div>`;
          list.querySelectorAll<HTMLButtonElement>(".chase-hit").forEach((b) =>
            b.addEventListener("click", () => pick(b.dataset.pid)),
          );
        };
        input.addEventListener("input", render);
        render();
      })
      .catch((e) => {
        list.innerHTML = `<div class="chase-modal-hint">Couldn't load the carer list — ${esc(
          e instanceof FunctionsError ? e.message : String(e),
        )}</div>`;
      });
  });
}
